/**
 * Display settings — §14.1, §14.2, §14.3.
 *
 * NONE of these bump `audioEpoch`. Restarting the recitation because the theme changed
 * is not a behaviour anyone asked for, and it is the single reason this slice takes
 * `set` rather than the `edit` the other two take.
 */
import { FONT_SCALE_DEFAULT, FONT_SCALE_MAX, FONT_SCALE_MIN } from '@mushaf/corpus'
import type { DisplayMode, DisplaySlice, SettingsSet, ThemeId } from './types'

export const THEMES: { id: ThemeId; label: string }[] = [
  { id: 'cream', label: 'ورقي' },
  { id: 'sepia', label: 'بُنّي' },
  { id: 'white', label: 'أبيض' },
  { id: 'green', label: 'أخضر' },
  { id: 'dark', label: 'ليلي' },
]

export const DISPLAY_MODES: { id: DisplayMode; label: string; hint: string }[] = [
  { id: 'glyph', label: 'المصحف (أبيض وأسود)', hint: 'خطّ المصحف الأصلي' },
  {
    id: 'tajweed',
    label: 'المصحف بالتجويد',
    hint: 'خطّ الملك فهد الملوّن — تلوين دقيق لكل حرف',
  },
  { id: 'word', label: 'تلوين الكلمات', hint: 'تقريبي: لون واحد لكل كلمة' },
  { id: 'text', label: 'نصّ بالتجويد', hint: 'نصّ يُعاد ترتيبه — لا يطابق تقسيم الصفحة' },
]

const clampScale = (n: number) =>
  Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, Math.round(n / 5) * 5))

/**
 * Each theme's `--surface`, which is the colour of the bar the mobile status bar sits
 * against (`TopBar` is `bg-[var(--surface)]`).
 *
 * DUPLICATED FROM `themes.css` ON PURPOSE, and it is the one place in the app where a
 * theme colour is written twice. `theme-color` is consumed by the OS chrome, not by the
 * page, so it cannot be a `var()` — the browser reads the attribute's literal value and
 * never resolves a custom property against our stylesheet. Kept beside the THEMES list
 * so the two are edited together; `pnpm contrast` reads `themes.css` and is unaffected.
 */
const THEME_COLOR: Record<ThemeId, string> = {
  cream: '#ffffff',
  sepia: '#f7efdf',
  white: '#ffffff',
  green: '#f3f8f0',
  dark: '#101214',
}

/**
 * The <html> attribute AND the `theme-color` meta, together, in ONE function.
 *
 * `theme-color` was a hardcoded cream `#fbf6e9` in both `index.html` and the manifest,
 * so an installed PWA on the dark paper kept a cream status bar — the one surface the
 * five-theme system never reached. It is a projection of `theme`, exactly as the
 * attribute is, so it gets the same single-writer treatment rather than a subscriber.
 */
export function applyTheme(theme: ThemeId): void {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme])
}

export const createDisplaySlice = (set: SettingsSet): DisplaySlice => ({
  theme: 'cream',
  mode: 'glyph',
  fontScale: FONT_SCALE_DEFAULT,

  /**
   * The <html> attribute is written HERE, not by a subscriber: the attribute is a
   * projection of this one value and giving it a second writer is how it goes out of
   * step with the store. `./index.ts` applies it once at module scope for first paint.
   */
  setTheme: (theme) => {
    applyTheme(theme)
    set({ theme })
  },
  setMode: (mode) => set({ mode }),
  setFontScale: (fontScale) => set({ fontScale: clampScale(fontScale) }),
})
